import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, Image, LogOut, MousePointerClick, Music, Sparkles, Trash2, Type, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { brandingFileUrl, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

const panelMotion = (index) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: 0.12 * index, ease: [0.22, 1, 0.36, 1] },
});

function AssetPanel({ kind, title, hint, icon, url, busy, onUpload, onRemove, testid }) {
  const inputRef = useRef(null);
  return (
    <motion.section className="studio-panel" {...panelMotion(kind === "logo" ? 1 : 2)} data-testid={`${testid}-panel`}>
      <div className="panel-head">{icon}<div><h2>{title}</h2><p>{hint}</p></div></div>
      <div className="asset-stage">
        {url ? (
          <img src={url} alt={`${title} preview`} data-testid={`${testid}-preview`} className={kind === "favicon" ? "favicon-preview" : "logo-preview"} />
        ) : (
          <div className="asset-empty" data-testid={`${testid}-empty`}><Sparkles size={16} /><span>Nothing uploaded yet</span></div>
        )}
      </div>
      <div className="asset-actions">
        <input ref={inputRef} type="file" accept={kind === "favicon" ? "image/*,.ico" : "image/*"} hidden data-testid={`${testid}-file-input`}
          onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" className="studio-btn" data-testid={`${testid}-upload-button`} disabled={busy} onClick={() => inputRef.current?.click()}>
          <Upload size={13} /> {busy ? "Uploading…" : url ? `Replace ${kind}` : `Upload ${kind}`}
        </button>
        {url && (
          <button type="button" className="studio-btn ghost" data-testid={`${testid}-remove-button`} disabled={busy} onClick={onRemove}>
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>
    </motion.section>
  );
}

function AudioPanel({ url, busy, onUpload, onRemove }) {
  const inputRef = useRef(null);
  return (
    <motion.section className="studio-panel wide" {...panelMotion(3)} data-testid="audio-panel">
      <div className="panel-head"><Music size={18} /><div><h2>Birthday music</h2><p>Starts playing the moment the secret number opens the celebration. MP3, WAV, OGG or M4A, up to 10 MB.</p></div></div>
      <div className="asset-stage">
        {url ? (
          <audio controls src={url} data-testid="audio-preview" style={{ width: "100%" }} />
        ) : (
          <div className="asset-empty" data-testid="audio-empty"><Music size={16} /><span>The default instrumental is playing for now</span></div>
        )}
      </div>
      <div className="asset-actions">
        <input ref={inputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" hidden data-testid="audio-file-input"
          onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" className="studio-btn" data-testid="audio-upload-button" disabled={busy} onClick={() => inputRef.current?.click()}>
          <Upload size={13} /> {busy ? "Uploading…" : url ? "Replace audio" : "Upload audio"}
        </button>
        {url && (
          <button type="button" className="studio-btn ghost" data-testid="audio-remove-button" disabled={busy} onClick={onRemove}>
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>
    </motion.section>
  );
}

export default function AdminDashboard() {
  const { user, setUser } = useAuth();
  const { branding, refresh } = useBranding();
  const navigate = useNavigate();
  const [siteName, setSiteName] = useState("");
  const [browserTitle, setBrowserTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [faviconBusy, setFaviconBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);

  useEffect(() => {
    if (branding) {
      setSiteName(branding.site_name || "");
      setBrowserTitle(branding.browser_title || "");
    }
  }, [branding]);

  const save = async (event) => {
    event.preventDefault();
    if (!siteName.trim() || !browserTitle.trim()) {
      toast.error("Name and browser title cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/branding", { site_name: siteName.trim(), browser_title: browserTitle.trim() });
      await refresh();
      toast.success("Branding saved — live across the whole site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upload = (kind, setBusyFn) => async (file) => {
    if (!file) return;
    setBusyFn(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/admin/branding/${kind}`, formData);
      await refresh();
      toast.success(`${kind === "logo" ? "Logo" : kind === "audio" ? "Birthday music" : "Favicon"} updated`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyFn(false);
    }
  };

  const remove = (kind) => async () => {
    try {
      await api.delete(`/admin/branding/${kind}`);
      await refresh();
      toast.success(`${kind === "logo" ? "Logo" : kind === "audio" ? "Birthday music" : "Favicon"} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
      navigate("/admin/login");
    }
  };

  return (
    <main className="studio-page" data-testid="admin-dashboard">
      <header className="studio-topbar">
        <div className="admin-mark"><Sparkles size={14} /> SITE STUDIO</div>
        <div className="studio-user">
          <span data-testid="admin-user-email">{user?.email}</span>
          <button type="button" className="studio-btn ghost" data-testid="admin-logout-button" onClick={logout}><LogOut size={13} /> Sign out</button>
        </div>
      </header>

      <motion.div className="studio-hero" {...panelMotion(0)}>
        <p className="eyebrow gold">Branding atelier</p>
        <h1 data-testid="admin-dashboard-title">Make it<br /><em>unmistakably yours.</em></h1>
        <p className="admin-sub">Every change below is saved forever and appears instantly on the public celebration — the name, the browser tab, the logo, the little icon in the tab, and the birthday song itself.</p>
      </motion.div>

      <div className="studio-grid">
        <motion.section className="studio-panel wide" {...panelMotion(1)} data-testid="identity-panel">
          <div className="panel-head"><Type size={18} /><div><h2>Name & browser title</h2><p>Shown in the footer, the tab, and anywhere the site introduces itself.</p></div></div>
          <form onSubmit={save} className="admin-form">
            <label htmlFor="site-name">WEBSITE NAME</label>
            <input id="site-name" data-testid="site-name-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} maxLength={80} placeholder="Saiswari Sahoo" />
            <label htmlFor="browser-title">BROWSER TAB TITLE</label>
            <input id="browser-title" data-testid="browser-title-input" value={browserTitle} onChange={(e) => setBrowserTitle(e.target.value)} maxLength={120} placeholder="Happy 18th Birthday, Saiswari" />
            <button type="submit" data-testid="branding-save-button" disabled={saving}>
              <MousePointerClick size={13} /> {saving ? "Saving…" : "Save branding"}
            </button>
          </form>
          <div className="tab-preview" data-testid="tab-preview">
            <Globe size={13} />
            <span className="tab-preview-title">{browserTitle || "Untitled"}</span>
            <span className="tab-preview-note">live tab preview</span>
          </div>
        </motion.section>

        <AssetPanel kind="logo" testid="logo" title="Logo" hint="Appears in the top corner of the celebration." icon={<Image size={18} />}
          url={brandingFileUrl(branding?.logo_url)} busy={logoBusy} onUpload={upload("logo", setLogoBusy)} onRemove={remove("logo")} />
        <AssetPanel kind="favicon" testid="favicon" title="Favicon" hint="The tiny icon in the browser tab. PNG or ICO works best." icon={<Globe size={18} />}
          url={brandingFileUrl(branding?.favicon_url)} busy={faviconBusy} onUpload={upload("favicon", setFaviconBusy)} onRemove={remove("favicon")} />
        <AudioPanel url={brandingFileUrl(branding?.audio_url)} busy={audioBusy} onUpload={upload("audio", setAudioBusy)} onRemove={remove("audio")} />
      </div>

      <footer className="studio-footer"><a href="/" data-testid="studio-view-site-link">View the celebration →</a></footer>
    </main>
  );
}
